import type { OneTimeActivity, Period, RecurringActivity } from '@life-rpg/shared';
import type { SupabaseClient } from '@supabase/supabase-js';

import type { ActivityInsert, ActivityRow, Database } from './database.types.js';

export type RecurringActivityDraft = Omit<RecurringActivity, 'id' | 'type'>;

export interface RecurringActivityRepository {
  list: () => Promise<RecurringActivity[]>;
  create: (draft: RecurringActivityDraft) => Promise<RecurringActivity>;
  update: (id: string, draft: RecurringActivityDraft) => Promise<RecurringActivity>;
  archive: (id: string) => Promise<void>;
}

export type OneTimeActivityDraft = Pick<OneTimeActivity, 'name' | 'difficulty'>;

export interface OneTimeActivityRepository {
  listActive: () => Promise<OneTimeActivity[]>;
  listCompleted: () => Promise<OneTimeActivity[]>;
  create: (draft: OneTimeActivityDraft) => Promise<OneTimeActivity>;
  update: (id: string, draft: OneTimeActivityDraft) => Promise<OneTimeActivity>;
  complete: (id: string) => Promise<OneTimeActivity>;
  archive: (id: string) => Promise<void>;
  convert: (
    id: string,
    draft: RecurringActivityDraft,
    completionQty?: number,
  ) => Promise<RecurringActivity>;
}

const COLUMNS =
  'id, type, name, unit_label, quick_add, target_qty, target_every, target_per, target_starts_on, target_day_of_month, difficulty, completed_at' as const;

type RecurringActivityRowInput = Pick<
  ActivityRow,
  | 'id'
  | 'type'
  | 'name'
  | 'unit_label'
  | 'quick_add'
  | 'target_qty'
  | 'target_every'
  | 'target_per'
  | 'target_starts_on'
  | 'target_day_of_month'
  | 'difficulty'
  | 'completed_at'
>;

type RecurringActivityRow = RecurringActivityRowInput & {
  type: 'recurring';
  unit_label: string;
  quick_add: number[];
  target_qty: number;
  target_every: number;
  target_per: Period;
  target_starts_on: string;
  target_day_of_month: number | null;
  completed_at: null;
};

function assertRecurringActivityRow(
  row: RecurringActivityRowInput,
): asserts row is RecurringActivityRow {
  if (
    row.type !== 'recurring' ||
    row.unit_label === null ||
    row.quick_add === null ||
    row.target_qty === null ||
    row.target_every === null ||
    row.target_per === null ||
    row.target_starts_on === null ||
    (row.target_per === 'month'
      ? row.target_day_of_month === null
      : row.target_day_of_month !== null) ||
    row.completed_at !== null
  ) {
    throw new Error('Supabase returned an invalid recurring Activity.');
  }
}

const fromRow = (row: RecurringActivityRowInput): RecurringActivity => {
  assertRecurringActivityRow(row);

  return {
    id: row.id,
    type: row.type,
    name: row.name,
    unitLabel: row.unit_label,
    quickAdd: row.quick_add,
    target: {
      qty: row.target_qty,
      every: row.target_every,
      per: row.target_per,
      startsOn: row.target_starts_on,
      ...(row.target_day_of_month === null ? {} : { dayOfMonth: row.target_day_of_month }),
    },
    difficulty: row.difficulty,
  };
};

const toRow = (draft: RecurringActivityDraft): ActivityInsert => ({
  type: 'recurring' as const,
  name: draft.name,
  unit_label: draft.unitLabel,
  quick_add: draft.quickAdd,
  target_qty: draft.target.qty,
  target_every: draft.target.every,
  target_per: draft.target.per,
  target_starts_on: draft.target.startsOn,
  target_day_of_month: draft.target.dayOfMonth ?? null,
  difficulty: draft.difficulty,
});

export const createSupabaseRecurringActivityRepository = (
  client: SupabaseClient<Database>,
): RecurringActivityRepository => ({
  list: async () => {
    const { data, error } = await client
      .from('activities')
      .select(COLUMNS)
      .eq('type', 'recurring')
      .is('archived_at', null)
      .order('name', { ascending: true });
    if (error) throw error;
    return data.map(fromRow);
  },

  create: async (draft) => {
    const { data, error } = await client
      .from('activities')
      .insert(toRow(draft))
      .select(COLUMNS)
      .single();
    if (error) throw error;
    return fromRow(data);
  },

  update: async (id, draft) => {
    const { data, error } = await client
      .from('activities')
      .update(toRow(draft))
      .eq('id', id)
      .eq('type', 'recurring')
      .is('archived_at', null)
      .select(COLUMNS)
      .single();
    if (error) throw error;
    return fromRow(data);
  },

  archive: async (id) => {
    const { error } = await client
      .from('activities')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', id)
      .eq('type', 'recurring')
      .is('archived_at', null);
    if (error) throw error;
  },
});

type OneTimeActivityRowInput = Pick<
  ActivityRow,
  | 'id'
  | 'type'
  | 'name'
  | 'unit_label'
  | 'quick_add'
  | 'target_qty'
  | 'target_every'
  | 'target_per'
  | 'target_starts_on'
  | 'target_day_of_month'
  | 'difficulty'
  | 'completed_at'
>;

type OneTimeActivityRow = OneTimeActivityRowInput & {
  type: 'one-time';
  unit_label: null;
  quick_add: null;
  target_qty: null;
  target_every: null;
  target_per: null;
  target_starts_on: null;
  target_day_of_month: null;
};

function assertOneTimeActivityRow(row: OneTimeActivityRowInput): asserts row is OneTimeActivityRow {
  if (
    row.type !== 'one-time' ||
    row.unit_label !== null ||
    row.quick_add !== null ||
    row.target_qty !== null ||
    row.target_every !== null ||
    row.target_per !== null ||
    row.target_starts_on !== null ||
    row.target_day_of_month !== null
  ) {
    throw new Error('Supabase returned an invalid one-time Activity.');
  }
}

const oneTimeFromRow = (row: OneTimeActivityRowInput): OneTimeActivity => {
  assertOneTimeActivityRow(row);

  return {
    id: row.id,
    type: row.type,
    name: row.name,
    difficulty: row.difficulty,
    ...(row.completed_at === null ? {} : { completedAt: row.completed_at }),
  };
};

const oneTimeToRow = (draft: OneTimeActivityDraft): ActivityInsert => ({
  type: 'one-time',
  name: draft.name,
  difficulty: draft.difficulty,
});

export const createSupabaseOneTimeActivityRepository = (
  client: SupabaseClient<Database>,
): OneTimeActivityRepository => ({
  listActive: async () => {
    const { data, error } = await client
      .from('activities')
      .select(COLUMNS)
      .eq('type', 'one-time')
      .is('completed_at', null)
      .is('archived_at', null)
      .order('name', { ascending: true });
    if (error) throw error;
    return data.map(oneTimeFromRow);
  },

  listCompleted: async () => {
    const { data, error } = await client
      .from('activities')
      .select(COLUMNS)
      .eq('type', 'one-time')
      .not('completed_at', 'is', null)
      .is('archived_at', null)
      .order('completed_at', { ascending: false });
    if (error) throw error;
    return data.map(oneTimeFromRow);
  },

  create: async (draft) => {
    const { data, error } = await client
      .from('activities')
      .insert(oneTimeToRow(draft))
      .select(COLUMNS)
      .single();
    if (error) throw error;
    return oneTimeFromRow(data);
  },

  update: async (id, draft) => {
    const { data, error } = await client
      .from('activities')
      .update(oneTimeToRow(draft))
      .eq('id', id)
      .eq('type', 'one-time')
      .is('completed_at', null)
      .is('archived_at', null)
      .select(COLUMNS)
      .single();
    if (error) throw error;
    return oneTimeFromRow(data);
  },

  complete: async (id) => {
    const { data, error } = await client
      .from('activities')
      .update({ completed_at: new Date().toISOString() })
      .eq('id', id)
      .eq('type', 'one-time')
      .is('completed_at', null)
      .is('archived_at', null)
      .select(COLUMNS)
      .single();
    if (error) throw error;
    return oneTimeFromRow(data);
  },

  archive: async (id) => {
    const { error } = await client
      .from('activities')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', id)
      .eq('type', 'one-time')
      .is('completed_at', null)
      .is('archived_at', null);
    if (error) throw error;
  },

  convert: async (id, draft, completionQty) => {
    const { data, error } = await client
      .rpc('convert_one_time_activity', {
        p_activity_id: id,
        p_name: draft.name,
        p_difficulty: draft.difficulty,
        p_unit_label: draft.unitLabel,
        p_quick_add: draft.quickAdd,
        p_target_qty: draft.target.qty,
        p_target_every: draft.target.every,
        p_target_per: draft.target.per,
        p_target_starts_on: draft.target.startsOn,
        p_target_day_of_month: draft.target.dayOfMonth ?? null,
        p_completion_qty: completionQty ?? null,
      })
      .single();
    if (error) throw error;
    return fromRow(data);
  },
});
